<template>
  <div
    ref="listElement"
    class="irp-contact-list"
    :class="{
      'irp-contact-list--own': isOwnData,
      'irp-contact-list--without-action': !hasActionColumn,
    }"
    data-testid="irp-contact-list"
  >
    <BittsEmptyState
      v-if="!rows.length"
      class="irp-contact-list__empty"
      :font-awesome-icon="EMPTY_STATE.icon"
      :title="emptyMessage"
      :description="EMPTY_STATE.description"
      data-testid="irp-contact-list-empty"
    />
    <template v-else>
      <template v-for="(row, index) in rows" :key="`contact_${index}`">
        <div
          class="irp-contact-list__row"
          tabindex="-1"
          :class="{ 'irp-contact-list__row--focused': row.isFocused }"
          data-testid="irp-contact-row"
        >
          <div class="irp-contact-list__cell" data-testid="irp-contact-name">
            <span class="irp-contact-list__label">
              {{ row.name.label }}
            </span>
            <div class="irp-contact-list__name">
              <FontAwesomeIcon
                :icon="['fak', 'person']"
                class="irp-contact-list__icon"
              />
              <IRPFieldGridText :text="row.name.value" :is-masked="true" />
            </div>
          </div>

          <div class="irp-contact-list__cell" data-testid="irp-contact-role">
            <span class="irp-contact-list__label">
              {{ row.role.label }}
            </span>
            <IRPFieldGridText :text="row.role.value" :is-masked="true" />
          </div>

          <div class="irp-contact-list__cell" data-testid="irp-contact-email">
            <span class="irp-contact-list__label">
              {{ row.email.label }}
            </span>
            <a
              v-if="row.email.value !== EMPTY_FIELD_VALUE"
              :href="`mailto:${row.email.value}`"
              :aria-label="`Email ${row.name.value}`"
              class="irp-contact-list__email"
            >
              <IRPFieldGridText :text="row.email.value" :is-masked="true" />
            </a>
            <IRPFieldGridText v-else :text="EMPTY_FIELD_VALUE" />
          </div>

          <div
            v-for="cell in row.cells"
            :key="cell.key"
            class="irp-contact-list__cell"
            :data-testid="`irp-contact-${cell.key}`"
          >
            <span class="irp-contact-list__label">{{ cell.label }}</span>
            <IRPFieldGridText :text="cell.value" :is-masked="true" />
          </div>

          <AddToCrm
            v-if="canAddToCrm"
            :contact="row.addToCrmContact"
            :record-id="recordId"
            :account="recordAccount"
            variant="ghost"
            class="irp-contact-list__add-to-crm"
            data-testid="irp-contact-add-to-crm"
            @added="(crmId) => markAddedToCrm(row.addToCrmContact.email, crmId)"
          />

          <div v-if="isOwnData" class="irp-contact-list__row-toggle">
            <IRPRowFieldsToggle
              v-if="row.remainingFields.length"
              :is-open="row.isOpen"
              :row-name="row.name.value"
              data-testid="irp-contact-row-toggle"
              @update:is-open="(isOpen) => setRowOpen(index, isOpen)"
            />
          </div>

          <IRPFieldGrid
            v-if="row.isOpen && row.remainingFields.length"
            :fields="row.remainingFields"
            class="irp-contact-list__all-fields"
          />
        </div>

        <div
          v-if="index < rows.length - 1"
          class="irp-contact-list__separator"
        />
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { BittsEmptyState } from '@crossbeam/bitts';
import type { BittsIcon } from '@crossbeam/bitts';

import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';

import AddToCrm from '@/components/sales/AccountDrawer/components/ContactsTab/AddToCrm.vue';

import { MDM_PROPERTIES, MDM_TYPES } from '@/constants/mdm';
import { useCopilotContactsStore, useIndividualRecordStore } from '@/stores';
import type { RecordContactType } from '@/types/individual_record';

import {
  EMPTY_FIELD_VALUE,
  addToCrmContactFromRecordContact,
  contactEmailOf,
  fieldCell,
  remainingFields,
} from './irp';
import IRPFieldGrid from './IRPFieldGrid.vue';
import IRPFieldGridText from './IRPFieldGridText.vue';
import IRPRowFieldsToggle from './IRPRowFieldsToggle.vue';

const {
  contacts,
  isOwnData = false,
  emptyMessage = 'No contacts to show',
} = defineProps<{
  contacts: RecordContactType[];
  isOwnData?: boolean;
  emptyMessage?: string;
}>();

const EMPTY_STATE: { description: string; icon: BittsIcon } = {
  description: 'Contacts for this account will appear here.',
  icon: ['fak', 'contacts'],
};

const HIGHLIGHT_DURATION_MS = 1500;

const CONTACT_PROPERTIES = MDM_PROPERTIES[MDM_TYPES.CONTACT];

const CELLS = [
  {
    key: 'phone',
    mdmProperty: CONTACT_PROPERTIES.PHONE,
    fallbackLabel: 'Phone',
  },
  {
    key: 'created-at',
    mdmProperty: CONTACT_PROPERTIES.CREATED_AT,
    fallbackLabel: 'Created at',
  },
  {
    key: 'last-activity',
    mdmProperty: CONTACT_PROPERTIES.LAST_ACTIVITY_AT,
    fallbackLabel: 'Last activity',
  },
];

const LISTED_PROPERTIES: string[] = [
  CONTACT_PROPERTIES.NAME,
  CONTACT_PROPERTIES.TITLE,
  CONTACT_PROPERTIES.EMAIL,
  ...CELLS.map(({ mdmProperty }) => mdmProperty),
];

const individualRecordStore = useIndividualRecordStore();
const copilotContactsStore = useCopilotContactsStore();

const recordId = computed(() => individualRecordStore.sourcePrimaryKey ?? '');

const canAddToCrm = computed(
  () => !isOwnData && individualRecordStore.mdmType !== 'lead',
);

const hasActionColumn = computed(() => canAddToCrm.value || isOwnData);

const recordAccount = computed(() => ({
  id: recordId.value,
  name: individualRecordStore.recordName,
  domain: individualRecordStore.recordDomain,
}));

function markAddedToCrm(email: string, crmId: string) {
  individualRecordStore.markContactInCrm(email, crmId);
  copilotContactsStore.refreshCopilotContactsStore();
}

const isAllExpanded = ref(false);
const openOverrides = ref<Record<number, boolean>>({});

function isRowOpen(index: number) {
  return !isOwnData || (openOverrides.value[index] ?? isAllExpanded.value);
}

function setRowOpen(index: number, isOpen: boolean) {
  openOverrides.value[index] = isOpen;
}

function setAllRowsOpen(isOpen: boolean) {
  isAllExpanded.value = isOpen;
  openOverrides.value = {};
}

const rows = computed(() =>
  contacts.map((contact, index) => ({
    name: fieldCell(contact.fields, CONTACT_PROPERTIES.NAME, 'Name'),
    role: fieldCell(contact.fields, CONTACT_PROPERTIES.TITLE, 'Role'),
    email: fieldCell(contact.fields, CONTACT_PROPERTIES.EMAIL, 'Email'),
    cells: CELLS.map(({ key, mdmProperty, fallbackLabel }) => ({
      key,
      ...fieldCell(contact.fields, mdmProperty, fallbackLabel),
    })),
    addToCrmContact: addToCrmContactFromRecordContact(contact),
    remainingFields: remainingFields(contact.fields, LISTED_PROPERTIES),
    isFocused: isFocusedContact(contact),
    isOpen: isRowOpen(index),
  })),
);

const expandableRows = computed(() =>
  rows.value.filter((row) => row.remainingFields.length),
);

const hasHiddenFields = computed(() => !!expandableRows.value.length);

const isEveryRowOpen = computed(
  () =>
    hasHiddenFields.value && expandableRows.value.every((row) => row.isOpen),
);

function isFocusedContact(contact: RecordContactType) {
  const focusedEmail = individualRecordStore.focusedContactEmail;
  return (
    !isOwnData && !!focusedEmail && contactEmailOf(contact) === focusedEmail
  );
}

const listElement = useTemplateRef<HTMLElement>('listElement');

let highlightTimeout: ReturnType<typeof setTimeout> | undefined;

onMounted(() => {
  const focusedRow = listElement.value?.querySelector<HTMLElement>(
    '.irp-contact-list__row--focused',
  );
  if (!focusedRow) return;
  focusedRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
  focusedRow.focus({ preventScroll: true });
  highlightTimeout = setTimeout(
    () => individualRecordStore.focusContact(null),
    HIGHLIGHT_DURATION_MS,
  );
});

onBeforeUnmount(() => clearTimeout(highlightTimeout));

defineExpose({ hasHiddenFields, isEveryRowOpen, setAllRowsOpen });
</script>

<style lang="pcss" scoped>
@reference "../../../css/theme.css";

.irp-contact-list {
  @apply grid items-center gap-x-24 gap-y-16;
  grid-template-columns: repeat(7, minmax(0, 1fr));
}

.irp-contact-list--own {
  grid-template-columns: repeat(6, minmax(0, 1fr)) auto;
}

.irp-contact-list--without-action {
  grid-template-columns: repeat(6, minmax(0, 1fr));
}

.irp-contact-list__row {
  @apply grid items-center gap-x-24 gap-y-16 rounded-8 outline-solid outline-2 outline-offset-8 outline-transparent transition-[outline-color] duration-500;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
}

.irp-contact-list__row--focused {
  @apply outline-secondary-border;
}

.irp-contact-list__empty {
  grid-column: 1 / -1;
}

.irp-contact-list__cell {
  @apply flex min-w-0 flex-col gap-4 text-base text-neutral-text;
}

.irp-contact-list__label {
  @apply truncate text-sm font-semibold text-neutral-text-weak;
}

.irp-contact-list__name {
  @apply flex min-w-0 items-center gap-8;
}

.irp-contact-list__icon {
  @apply shrink-0 text-neutral-accent;
}

.irp-contact-list__all-fields {
  grid-column: 1 / -2;
}

.irp-contact-list--without-action .irp-contact-list__all-fields {
  grid-column: 1 / -1;
}

.irp-contact-list__separator {
  @apply border-t border-neutral-border;
  grid-column: 1 / -1;
}

.irp-contact-list__add-to-crm,
.irp-contact-list__row-toggle {
  @apply flex justify-end;
}

.irp-contact-list__email {
  @apply min-w-0 text-secondary-text-link hover:text-secondary-text-link-hover;
}
</style>
